import {ReactElement} from "react";
import {Col, Row} from "@zendeskgarden/react-grid";
import * as css from "./styles/payrollSettings.module.scss";
import {Button} from "@zendeskgarden/react-buttons";
import {Body, Cell, Head, HeaderCell, HeaderRow, Row as TableRow, Table} from "@zendeskgarden/react-tables";
import {Hint, Input, MediaInput, Label as FormLabel} from "@zendeskgarden/react-forms";
import {PayrollPayFrequency} from "../../__generated__/generated_types";
import {buildCustomPresetDates, buildDatesForMonthByCount, MonthReference, sortAndDedupeDates, wrapDateIntoRange, YearSegment} from "./logic/payroll-custom-dates";
import {addDays} from "./logic/payroll-dates";
import {getLastDayOfMonth, getMonths} from "../../utils/months-helper";

// NOTE: getMonths is able to provide month labels based on property.
// However payroll settings are client level and it is not correct to assume one
// of the properties to build month labels so we use property-less call to the getMonths
const monthLabels = getMonths();
const dayNumbers = Array.from({length: 31}, (_, index) => index + 1);

type CustomPayDatesEditorProps = {
    budgetYear: number;
    monthReferences: MonthReference[];
    customDatesByMonth: Date[][];
    presetFrequencies: PayrollPayFrequency[];
    setCustomPayDates: (value: React.SetStateAction<Date[]>) => void;
    customPayDates: Date[];
};

export function CustomPayDatesEditor(props: CustomPayDatesEditorProps): ReactElement {
    const {
        budgetYear,
        monthReferences,
        customDatesByMonth,
        setCustomPayDates,
        customPayDates,
        presetFrequencies
    } = props;

    function setDatesForMonth(monthOffset: number, dates: Date[]) {
        if(!budgetYear) {
            return;
        }
        const monthRef = monthReferences[monthOffset];
        if(!monthRef) {
            return;
        }
        const next = customDatesByMonth.map((monthDates, index) => {
            if(index == monthOffset) {
                return sortAndDedupeDates(dates)
                    .filter(date => date.getUTCFullYear() == monthRef.year && date.getUTCMonth() == monthRef.monthIndex);
            }
            return monthDates;
        }).flat();
        setCustomPayDates(sortAndDedupeDates(next));
    }

    function onShiftDates(days: number) {
        if(!budgetYear) {
            return;
        }
        const rangeStart = new Date(Date.UTC(budgetYear - 1, 0, 1));
        const rangeEnd = new Date(Date.UTC(budgetYear, 11, 31));
        const shifted = customPayDates
            .map(date => wrapDateIntoRange(addDays(date, days), rangeStart, rangeEnd));
        setCustomPayDates(sortAndDedupeDates(shifted));
    }

    function onSetMonthCount(monthOffset: number, count: number) {
        const monthRef = monthReferences[monthOffset];
        if(!monthRef) {
            return;
        }
        setDatesForMonth(
            monthOffset,
            buildDatesForMonthByCount(
                monthRef.year,
                monthRef.monthIndex,
                count
            )
        );
    }

    function onToggleDay(monthOffset: number, day: number) {
        const monthRef = monthReferences[monthOffset];
        if(!monthRef) {
            return;
        }
        const monthLastDay = getLastDayOfMonth(monthRef.monthIndex, monthRef.year);
        if(day < 1 || day > monthLastDay) {
            return;
        }
        const currentMonthDates = customDatesByMonth[monthOffset] ?? [];
        const hasDate = currentMonthDates.some(date => date.getUTCDate() == day);
        const nextMonthDates = hasDate
            ? currentMonthDates.filter(date => date.getUTCDate() != day)
            : [...currentMonthDates, new Date(Date.UTC(monthRef.year, monthRef.monthIndex, day))];
        setDatesForMonth(monthOffset, nextMonthDates);
    }


    function onApplyPreset(preset: PayrollPayFrequency) {
        if(!budgetYear) {
            return;
        }
        setCustomPayDates(buildCustomPresetDates(preset, budgetYear));
    }

    return (
        <Row className={css.settingsRow}>
            <Col>
                <Hint>Choose a preset, then adjust by specific dates or by number of pay days for each month in reforecast and budget years.</Hint>
                <div className={css.presetButtons}>
                    {presetFrequencies.map(preset => (
                        <Button key={preset} onClick={() => onApplyPreset(preset)}>
                            {preset.replace(/_/g, " ")}
                        </Button>
                    ))}
                </div>
                <div className={css.customControls}>
                    <div className={css.customControlGroup}>
                        <FormLabel>Roll Dates</FormLabel>
                        <div className={css.moveButtons}>
                            <Button onClick={() => onShiftDates(-3)}>-3 days</Button>
                            <Button onClick={() => onShiftDates(-2)}>-2 days</Button>
                            <Button onClick={() => onShiftDates(-1)}>-1 day</Button>
                            <Button onClick={() => onShiftDates(1)}>+1 day</Button>
                            <Button onClick={() => onShiftDates(2)}>+2 days</Button>
                            <Button onClick={() => onShiftDates(3)}>+3 days</Button>
                        </div>
                    </div>
                </div>
                {(["reforecast", "budget"] as YearSegment[]).map((segment, segmentIdx) => {
                    return (
                        <div className={css.customYearSection} key={segment}>
                            <FormLabel>{segment == "reforecast" ? `${budgetYear - 1} Reforecast` : `${budgetYear} Budget`}</FormLabel>
                            <Table>
                                <Head>
                                    <HeaderRow>
                                        <HeaderCell width="8rem">Month</HeaderCell>
                                        <HeaderCell width="6rem">Count</HeaderCell>
                                        {dayNumbers.map(day => <HeaderCell key={day}>{day}</HeaderCell>)}
                                    </HeaderRow>
                                </Head>
                                <Body>
                                    {monthReferences
                                        .filter(monthRef => monthRef.segment == segment)
                                        .map(monthRef => {
                                            const monthOffset = (segment == "reforecast" ? 0 : 12) + monthRef.monthIndex;
                                            const monthDates = customDatesByMonth[monthOffset] ?? [];
                                            const monthLastDay = getLastDayOfMonth(monthRef.monthIndex, monthRef.year);
                                            const count = monthDates.length;

                                            return (
                                                <TableRow key={`${monthRef.year}-${monthRef.monthIndex}`}>
                                                    <Cell><b>{monthLabels[monthRef.monthIndex]}</b></Cell>
                                                    <Cell>
                                                        <MediaInput
                                                            type="number"
                                                            min={0}
                                                            max={31}
                                                            tabIndex={(segmentIdx + 1) * 100 + monthRef.monthIndex}
                                                            value={count === 0 ? "0" : String(count)}
                                                            onChange={event => {
                                                                const raw = event.target.value;
                                                                const next = raw === "" ? 0 : Number(raw);
                                                                onSetMonthCount(monthOffset, Number.isNaN(next) ? 0 : next);
                                                            }}
                                                        />
                                                    </Cell>
                                                    {dayNumbers.map(day => {
                                                        const isDayInMonth = day <= monthLastDay;
                                                        const isSelected = isDayInMonth && monthDates.some(date => date.getUTCDate() == day);
                                                        return (
                                                            <Cell
                                                                className={
                                                                    !isDayInMonth
                                                                        ? undefined
                                                                        : isSelected
                                                                            ? css.extraPeriodCell
                                                                            : undefined
                                                                }
                                                                key={day}
                                                            >
                                                                {isDayInMonth ?
                                                                    <button
                                                                        type="button"
                                                                        style={{width: "100%", border: "none", background: "transparent", cursor: "pointer", padding: "0.25rem 0.2rem", fontSize: "0.75rem"}}
                                                                        onClick={() => onToggleDay(monthOffset, day)}
                                                                    >
                                                                        {day}
                                                                    </button>
                                                                    : ""}
                                                            </Cell>
                                                        );
                                                    })}
                                                </TableRow>
                                            );
                                        })}
                                </Body>
                            </Table>
                        </div>
                    );
                })}
            </Col>
        </Row>
    );
}

